/** The Tours grid search namespaces wired to their suggestion fetchers.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators` on
 * TourFilterNode (contract-tested). `exact` is deliberately omitted wherever
 * `iexact` alone would serialise to `=` and re-parse to `iexact` (name/custom_id
 * are icontains+iexact only). `tag` and `launch_policy` keep BOTH `exact` and
 * `iexact` so the widget offers the "by ID" (`=#`) affordance (operators.ts
 * needs both). `id` and `num_bookings` follow the bookings `pax` precedent — a
 * compact value grammar under a single `=`, no `>`/`<` needed.
 *
 * Launch-policy and tag suggestions are per-tenant: the widget passes the
 * picker's `tour_operator_id` via extraFilterValues; no tenant ⇒ no suggestions
 * (typed names still filter across tenants — the picker only narrows the list).
 */
import type { NamespaceConfigMap } from '@/components/QuerySearch';
import * as toursApi from '@/api/tours';

/** == TourFilterNode x-trip-type-labels (contract-tested). */
export const TRIP_TYPE_LABELS = ['Tour', 'Bus trip', 'Exhibition'];
const YES_NO = ['yes', 'no'];

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

/** Suggest launch policies for the SELECTED tenant only (they're per-TO).
 *  Returns `{ id }` so the "by ID" commit works; no tenant ⇒ no suggestions. */
function fetchLaunchPolicies() {
  return async (query: string, extra: Record<string, string>, _signal: AbortSignal) => {
    const top = extra.tour_operator_id;
    if (!top || !/^\d+$/.test(top)) return [];
    const res = await toursApi.picker(Number(top), 'launch_policies', query);
    return res.results.map((p) => ({
      value: p.name ?? String(p.id),
      label: p.name ?? `#${p.id}`,
      id: p.id,
    }));
  };
}

/** Suggest tags for the SELECTED tenant only. */
function fetchTags() {
  return async (query: string, extra: Record<string, string>, _signal: AbortSignal) => {
    const top = extra.tour_operator_id;
    if (!top || !/^\d+$/.test(top)) return [];
    const res = await toursApi.picker(Number(top), 'tags', query);
    return res.results.map((t) => ({
      value: t.name ?? String(t.id),
      label: t.name ?? `#${t.id}`,
      id: t.id,
    }));
  };
}

export function buildTourNamespaces(): NamespaceConfigMap {
  return {
    name: {
      label: 'name',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Tour name.',
      examples: ['name=~sunset', 'name="City Highlights"'],
    },
    custom_id: {
      label: 'custom_id',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'External / custom ID.',
      examples: ['custom_id=ABC-12', 'custom_id=~ABC'],
    },
    tag: {
      label: 'tag',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: fetchTags(),
      help: 'Tour tag by name (groups same-named variants). Pick "by ID" on a suggestion to match one exact tag row. Suggestions need a tenant selected.',
      examples: ['tag=~vip', 'tag=Balance due', 'tag=#906'],
    },
    launch_policy: {
      label: 'launch_policy',
      operators: ['iexact', 'exact'],
      valueType: 'string',
      fetchSuggestions: fetchLaunchPolicies(),
      help: 'Launch policy by name (all-digits also matches its id). Suggestions need a tenant selected.',
      examples: ['launch_policy="Default"', 'launch_policy=#42'],
    },
    trip_type: {
      label: 'trip_type',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(TRIP_TYPE_LABELS),
      help: 'Tour / Bus trip / Exhibition (by label or 0/1/2).',
      examples: ['trip_type=Tour', 'trip_type=1'],
    },
    is_bespoke: {
      label: 'is_bespoke',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(YES_NO),
      help: 'Fixed-date (yes) vs recurring (no).',
      examples: ['is_bespoke=no'],
    },
    use_as_template: {
      label: 'use_as_template',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(YES_NO),
      help: 'Template tours (yes/no).',
      examples: ['use_as_template=yes'],
    },
    allow_selfreg: {
      label: 'allow_selfreg',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(YES_NO),
      help: 'Self-registration allowed (yes/no).',
      examples: ['allow_selfreg=no'],
    },
    has_group_chat: {
      label: 'has_group_chat',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(YES_NO),
      help: 'Tour has a group chat (yes/no).',
      examples: ['has_group_chat=yes'],
    },
    start_date: {
      label: 'start_date',
      operators: ['exact', 'range'],
      valueType: 'date',
      help: 'Fixed-date tour start (calendar date). `=` matches one day; a range takes two dates.',
      examples: ['start_date=2026-07-01', 'start_date=2026-06-01..2026-09-01'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When created, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['created=2026-01-01..2026-07-01'],
    },
    updated: {
      label: 'updated',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When last edited, UTC.',
      examples: ['updated=2026-07-20'],
    },
    num_bookings: {
      label: 'num_bookings',
      operators: ['exact'],
      valueType: 'string',
      help: 'Publishable booking count: a single value, an inclusive range, or open-ended.',
      examples: ['num_bookings=0', 'num_bookings=1-10', 'num_bookings=50+'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Tour id.',
      examples: ['id=8121'],
    },
  };
}
